import React, { useCallback, useEffect, useState } from "react";
import { Typography, Card, Form, Input, Button, Select, Switch, Tooltip, notification, Spin, Grid, Avatar, theme, Space } from "antd";
import { LockOutlined, LogoutOutlined, UserOutlined, EditOutlined, KeyOutlined, SafetyOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { cancelAccountDeletion, exportAccountData, getAccountDeletionStatus, requestAccountDeletion, usernatorClient as client } from "../hooks/useFriends";
import { UdpatePassword, UserData, FollowPolicy } from "../generated/user_pb";
import { StatusCode } from "grpc-web";
import type { RpcError } from "grpc-web";
import { ErrorState, PageHeader } from "@dowty/ui";
import { AccountDataPanel } from "@dowty/legal";

const { useBreakpoint } = Grid;
const { Text } = Typography;

// Form.Item clones its *direct* child to inject `checked`/`onChange` (per `valuePropName="checked"`
// below) — a bare Tooltip wrapping the Switch inline would swallow those props rather than forward
// them, silently breaking the binding. This small wrapper is Form.Item's direct child instead, and
// explicitly passes the injected props on to the real Switch it renders.
const AutoAcceptFollowBackSwitch: React.FC<{
    checked?: boolean;
    onChange?: (checked: boolean) => void;
    disabled?: boolean;
    tooltipTitle: string;
    label: string;
}> = ({ checked, onChange, disabled, tooltipTitle, label }) => (
    <Tooltip title={disabled ? tooltipTitle : ""}>
        <span>
            <Switch aria-label={label} checked={checked} onChange={onChange} disabled={disabled} />
        </span>
    </Tooltip>
);

const ProfilePage: React.FC = () => {
    const { user, logout, updateUser } = useAuth();
    const isGerman = navigator.language.toLowerCase().startsWith("de");
    const t = (de: string, en: string) => isGerman ? de : en;
    const navigate = useNavigate();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const { token } = theme.useToken();
    const [infoForm] = Form.useForm();
    const [pwForm] = Form.useForm();
    const [followForm] = Form.useForm();
    const [infoLoading, setInfoLoading] = useState(false);
    const [infoFetching, setInfoFetching] = useState(true);
    const [infoError, setInfoError] = useState<string | null>(null);
    const [infoRetry, setInfoRetry] = useState(0);
    const [loading, setLoading] = useState(false);
    const [followFetching, setFollowFetching] = useState(true);
    const [followError, setFollowError] = useState<string | null>(null);
    const [followRetry, setFollowRetry] = useState(0);
    const [followLoading, setFollowLoading] = useState(false);
    const [deletionScheduledFor, setDeletionScheduledFor] = useState<string | null>(null);
    const [deletionStatusLoading, setDeletionStatusLoading] = useState(true);
    const [deletionStatusError, setDeletionStatusError] = useState(false);
    const [api, contextHolder] = notification.useNotification();
    // Drives the auto-accept switch's disabled state — it only means anything under APPROVAL.
    const watchedFollowPolicy = Form.useWatch("followPolicy", followForm);

    const refreshDeletionStatus = useCallback(async () => {
        if (!user) return;
        setDeletionStatusLoading(true);
        setDeletionStatusError(false);
        try {
            setDeletionScheduledFor(await getAccountDeletionStatus(user));
        } catch {
            setDeletionStatusError(true);
        } finally {
            setDeletionStatusLoading(false);
        }
    }, [user]);

    useEffect(() => { void refreshDeletionStatus(); }, [refreshDeletionStatus]);

    useEffect(() => {
        if (!user) return;
        setInfoFetching(true);
        setInfoError(null);
        const u = new UserData();
        u.setUserid(user!.userId);
        u.setUsername(user!.username);
        u.setToken(user!.token);
        client.getUserInfo(u, {})
            .then(res => {
                const visibility = res.getVisibility();
                infoForm.setFieldsValue({
                    username: res.getUsername(),
                    name: res.getName(),
                    visibility: ({
                        PUBLIC: "Public", FRIENDS_ONLY: "Friends-only", PRIVATE: "Private",
                    } as Record<string, string>)[visibility.toUpperCase().replace("-", "_")] || visibility || "Public",
                });
            })
            .catch(() => {
                setInfoError(isGerman ? "Kontoinformationen konnten nicht geladen werden." : "Couldn't load account information.");
            })
            .finally(() => setInfoFetching(false));
    }, [user, infoForm, infoRetry, isGerman]);

    useEffect(() => {
        if (!user) return;
        setFollowFetching(true);
        setFollowError(null);
        const u = new UserData();
        u.setUserid(user!.userId);
        u.setUsername(user!.username);
        u.setToken(user!.token);
        client.getFollowSettings(u, {})
            .then(res => {
                // A user who has never opened this page has no UserInfo row values set; the server
                // answers with the real defaults (OPEN, off) rather than leaving fields unset, and
                // those getters already return that default when the underlying field is absent — so
                // this renders as a normal, already-decided state rather than an empty control.
                followForm.setFieldsValue({
                    followPolicy: res.getFollowpolicy(),
                    autoAcceptFollowBack: res.getAutoacceptfollowback(),
                });
            })
            .catch(() => {
                setFollowError(isGerman ? "Follower-Einstellungen konnten nicht geladen werden." : "Failed to load follow settings.");
            })
            .finally(() => setFollowFetching(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, followForm, followRetry, isGerman]);

    useEffect(() => {
        const reload = () => {
            if (infoError) setInfoRetry(attempt => attempt + 1);
            if (followError) setFollowRetry(attempt => attempt + 1);
            if (deletionStatusError) void refreshDeletionStatus();
        };
        window.addEventListener("online", reload);
        return () => window.removeEventListener("online", reload);
    }, [infoError, followError, deletionStatusError, refreshDeletionStatus]);

    const handleUpdateInfo = async (values: { username: string; name: string; visibility: string }) => {
        if (infoFetching || infoError) return;
        const trimmedUsername = values.username.trim();
        setInfoLoading(true);
        try {
            const u = new UserData();
            u.setUserid(user!.userId);
            u.setUsername(trimmedUsername);
            u.setToken(user!.token);
            u.setName(values.name.trim());
            u.setVisibility(values.visibility);
            const res = await client.updateUserInfo(u, {});
            // Users is a shared table — this rename is what Saufdings/Sportdings will show too.
            // The response carries a freshly-signed token for the new username; without adopting
            // it here, the next authenticated call would re-send the old username and the backend
            // would re-stamp every subsequent token back to it.
            updateUser({
                username: res.getUsername() || trimmedUsername,
                token: res.getToken() || user!.token,
            });
            infoForm.setFieldsValue({ username: res.getUsername() || trimmedUsername });
            api.success({ message: t("Profil aktualisiert.", "Profile updated."), description: t("Dein Benutzername wird in Saufdings, Sportdings und Dowtex verwendet und überall mit dem neuen Namen angezeigt.", "Your username is shared across Saufdings, Sportdings and Dowtex — it now shows the new name everywhere."), placement: "top" });
        } catch (err) {
            const rpc = err as RpcError;
            let description = isGerman ? "Bitte versuche es erneut. Dein Profil wurde nicht geändert." : "Please try again. Your profile has not been changed.";
            if (rpc.code === StatusCode.ALREADY_EXISTS) description = isGerman ? "Dieser Benutzername ist bereits vergeben." : "That username is already taken.";
            api.error({ message: isGerman ? "Profil konnte nicht aktualisiert werden." : "Failed to update profile.", description, placement: "top" });
        } finally {
            setInfoLoading(false);
        }
    };

    const handleUpdateFollowSettings = async (values: { followPolicy: FollowPolicy; autoAcceptFollowBack: boolean }) => {
        if (followFetching || followError) return;
        setFollowLoading(true);
        try {
            const u = new UserData();
            u.setUserid(user!.userId);
            u.setUsername(user!.username);
            u.setToken(user!.token);
            // Always send both fields explicitly, even coming from the never-opened-this-page
            // default state above, so the server never has to guess at an unset value.
            u.setFollowpolicy(values.followPolicy);
            u.setAutoacceptfollowback(values.autoAcceptFollowBack);
            await client.updateFollowSettings(u, {});
            api.success({ message: t("Follower-Einstellungen aktualisiert.", "Follow settings updated."), placement: "top" });
        } catch {
            api.error({ message: isGerman ? "Follower-Einstellungen konnten nicht aktualisiert werden." : "Failed to update follow settings.", description: isGerman ? "Bitte versuche es erneut. Deine Follower-Einstellungen wurden nicht geändert." : "Please try again. Your follow settings have not been changed.", placement: "top" });
        } finally {
            setFollowLoading(false);
        }
    };

    const hashSHA256 = async (input: string): Promise<string> => {
        const data = new TextEncoder().encode(input);
        const buf = await crypto.subtle.digest("SHA-256", data);
        return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, "0")).join("");
    };

    const handleChangePassword = async (values: { oldPassword: string; newPassword: string; confirmPassword: string }) => {
        setLoading(true);
        try {
            const userData = new UserData();
            userData.setUserid(user!.userId);
            userData.setUsername(user!.username);
            userData.setToken(user!.token);

            const req = new UdpatePassword();
            req.setUser(userData);
            req.setOldpassword(await hashSHA256(values.oldPassword));
            req.setNewpassword(await hashSHA256(values.newPassword));

            await client.updatePassword(req, {});
            api.success({ message: t("Passwort aktualisiert.", "Password updated."), placement: "top" });
            pwForm.resetFields();
        } catch {
            api.error({ message: isGerman ? "Passwort konnte nicht aktualisiert werden." : "Failed to update password.", description: isGerman ? "Prüfe dein aktuelles Passwort und versuche es erneut." : "Check your current password and try again.", placement: "top" });
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate("/login");
        } catch {
            api.error({ message: isGerman ? "Abmeldung fehlgeschlagen. Bitte erneut versuchen." : "Sign out failed. Please retry." });
        }
    };

    const initial = user?.username?.[0]?.toUpperCase() ?? "?";
    const language = isGerman ? "de" : "en";

    return (
        <div className="sf-content-column" style={{ padding: isMobile ? "16px" : "32px 24px" }}>
            {contextHolder}
            <PageHeader title={t("Profil", "Profile")} />

            {/* Hero / avatar section */}
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: 20,
                marginBottom: 32,
                padding: "24px",
                borderRadius: token.borderRadiusLG,
                background: token.colorPrimaryBg,
                border: `1px solid ${token.colorBorderSecondary}`,
            }}>
                <Avatar
                    size={72}
                    style={{
                        background: token.colorPrimary,
                        fontSize: 28,
                        fontWeight: 700,
                        flexShrink: 0,
                    }}
                >
                    {initial}
                </Avatar>
                <div>
                    <Text strong>{user?.username}</Text>
                </div>
            </div>

            <Card
                title={<Space><EditOutlined style={{ color: token.colorPrimary }} /><span>{t("Kontoinformationen", "Account Info")}</span></Space>}
                style={{ marginBottom: 20, borderTop: `2px solid ${token.colorPrimaryBorder}` }}
            >
                {infoError && <ErrorState message={infoError} retry={() => setInfoRetry(attempt => attempt + 1)} retryLabel={language === "de" ? "Erneut versuchen" : "Retry"} />}
                <Spin spinning={infoFetching}>
                    <Form form={infoForm} layout="vertical" onFinish={handleUpdateInfo} disabled={infoFetching || !!infoError} requiredMark={false}>
                        <Form.Item
                            name="username"
                            label={t("Benutzername", "Username")}
                            rules={[
                                { required: true, whitespace: true, message: t("Benutzername ist erforderlich.", "Username is required.") },
                                { min: 3 },
                            ]}
                            extra={t("Wird in Saufdings, Sportdings und Dowtex verwendet. Eine Änderung benennt dich überall um.", "Shared across Saufdings, Sportdings and Dowtex — changing it renames you everywhere.")}
                        >
                            <Input prefix={<UserOutlined />} />
                        </Form.Item>
                        <Form.Item name="name" label={t("Anzeigename", "Display name")} rules={[{ max: 255, message: isGerman ? "Maximal 255 Zeichen." : "Use at most 255 characters." }]}>
                            <Input prefix={<UserOutlined />} maxLength={255} showCount placeholder={t("Optional", "Optional")} />
                        </Form.Item>
                        <Form.Item name="visibility" label={t("Sichtbarkeit", "Visibility")}>
                            <Select options={[
                                { value: "Public",       label: language === "de" ? "Öffentlich — jeder kann dein Profil sehen" : "Public — anyone can see your profile" },
                                { value: "Friends-only", label: language === "de" ? "Nur Freunde" : "Friends only" },
                                { value: "Private",      label: language === "de" ? "Privat" : "Private" },
                            ]} />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" loading={infoLoading}>{isGerman ? "Profil speichern" : "Save profile"}</Button>
                    </Form>
                </Spin>
            </Card>

            <Card
                title={<Space><SafetyOutlined style={{ color: token.colorPrimary }} /><span>{t("Follower-Einstellungen", "Follow Privacy")}</span></Space>}
                style={{ marginBottom: 20, borderTop: `2px solid ${token.colorPrimaryBorder}` }}
            >
                {followError && <ErrorState message={followError} retry={() => setFollowRetry(attempt => attempt + 1)} retryLabel={language === "de" ? "Erneut versuchen" : "Retry"} />}
                <Spin spinning={followFetching}>
                    <Form form={followForm} layout="vertical" onFinish={handleUpdateFollowSettings} disabled={followFetching || !!followError} requiredMark={false}>
                        <Form.Item
                            name="followPolicy"
                            label={t("Wer darf dir folgen?", "Who can follow you")}
                        >
                            <Select options={[
                                { value: FollowPolicy.OPEN, label: t("Offen — jede Person kann dir sofort folgen", "Open — anyone can follow you immediately") },
                                { value: FollowPolicy.APPROVAL, label: t("Bestätigung erforderlich — du bestätigst oder lehnst jede Anfrage ab", "Approval required — you approve or reject each follow request") },
                            ]} />
                        </Form.Item>
                        <div className="sf-follow-setting-row">
                            <div>
                                <Text strong>{language === "de" ? "Rück-Follower automatisch annehmen" : "Auto-accept follow-back"}</Text>
                                <Text type="secondary" className="sf-follow-setting-description">
                                    {language === "de"
                                        ? "Wenn jemand, dem du bereits folgst, dir ebenfalls folgen möchte, wird die Anfrage automatisch angenommen."
                                        : "If someone you already follow asks to follow you, approve them automatically instead of leaving the request pending."}
                                </Text>
                            </div>
                            <Form.Item name="autoAcceptFollowBack" valuePropName="checked" noStyle>
                                <AutoAcceptFollowBackSwitch
                                    disabled={followFetching || !!followError || watchedFollowPolicy === FollowPolicy.OPEN}
                                    tooltipTitle={language === "de"
                                        ? 'Gilt nur, wenn "Wer darf dir folgen?" auf "Bestätigung erforderlich" steht — bei "Offen" folgt jeder sofort.'
                                        : 'Only applies when "Who can follow you" is set to Approval required — under Open, anyone follows immediately with no request to auto-accept.'}
                                    label={language === "de" ? "Automatisches Annehmen von Rück-Followern" : "Auto-accept follow-back"}
                                />
                            </Form.Item>
                        </div>
                        <Button type="primary" htmlType="submit" loading={followLoading}>{isGerman ? "Privatsphäre speichern" : "Save privacy"}</Button>
                    </Form>
                </Spin>
            </Card>

            <Card
                title={<Space><KeyOutlined style={{ color: token.colorPrimary }} /><span>{t("Passwort ändern", "Change Password")}</span></Space>}
                style={{ marginBottom: 20, borderTop: `2px solid ${token.colorPrimaryBorder}` }}
            >
                <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 16 }}>
                    {t("Eine Passwortänderung widerruft deine Anmeldetoken nicht. Du bleibst hier und in allen anderen Apps angemeldet. Das alte Passwort kann nur für künftige Anmeldungen nicht mehr verwendet werden.", "Your sign-in tokens aren't revoked by a password change, so you'll stay signed in here and in any other app you're already logged into. This only stops a future sign-in with the old password.")}
                </Text>
                <Form form={pwForm} layout="vertical" onFinish={handleChangePassword} requiredMark={false}>
                    <Form.Item name="oldPassword" label={t("Aktuelles Passwort", "Current password")} rules={[{ required: true, message: t("Bitte gib dein aktuelles Passwort ein.", "Please enter your current password.") }]}>
                        <Input.Password prefix={<LockOutlined />} autoComplete="current-password" />
                    </Form.Item>
                    <Form.Item
                        name="newPassword"
                        label={t("Neues Passwort", "New password")}
                        dependencies={["oldPassword"]}
                        rules={[
                            { required: true, message: t("Bitte gib ein neues Passwort ein.", "Please enter a new password.") },
                            { min: 6, message: t("Das Passwort muss mindestens 6 Zeichen lang sein.", "Password must be at least 6 characters.") },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || value !== getFieldValue("oldPassword")) return Promise.resolve();
                                    return Promise.reject(new Error(t("Das neue Passwort muss sich vom aktuellen Passwort unterscheiden.", "New password must be different from your current password.")));
                                },
                            }),
                        ]}
                    >
                        <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
                    </Form.Item>
                    <Form.Item
                        name="confirmPassword"
                        label={t("Neues Passwort bestätigen", "Confirm new password")}
                        dependencies={["newPassword"]}
                        rules={[
                            { required: true, message: t("Bitte bestätige dein neues Passwort.", "Please confirm your new password.") },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || value === getFieldValue("newPassword")) return Promise.resolve();
                                    return Promise.reject(new Error(t("Die Passwörter stimmen nicht überein.", "Passwords do not match.")));
                                },
                            }),
                        ]}
                    >
                        <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        {t("Passwort aktualisieren", "Update password")}
                    </Button>
                </Form>
            </Card>

            <Spin spinning={deletionStatusLoading} tip={t("Datenschutzeinstellungen werden geladen …", "Loading data settings...")}>
                {deletionStatusError && <ErrorState message={t("Der Status deiner Kontolöschung konnte nicht geladen werden.", "Could not load your deletion status.")} retry={() => void refreshDeletionStatus()} retryLabel={language === "de" ? "Erneut versuchen" : "Retry"} />}
                <div aria-busy={deletionStatusLoading} inert={deletionStatusLoading || deletionStatusError}>
                    <AccountDataPanel
                        language={language}
                        deletionScheduledFor={deletionScheduledFor}
                        onExport={() => exportAccountData(user!)}
                        onRequestDeletion={(password) => requestAccountDeletion(user!, password)}
                        onCancelDeletion={() => cancelAccountDeletion(user!)}
                        onChanged={() => void refreshDeletionStatus()}
                    />
                </div>
            </Spin>

            <Card
                style={{ borderTop: `2px solid ${token.colorErrorBorder}` }}
            >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                        <Text strong>{t("Abmelden", "Sign out")}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>{t("Du wirst zurück zur Anmeldeseite geleitet.", "You'll be taken back to the login screen.")}</Text>
                    </div>
                    <Button danger icon={<LogoutOutlined />} onClick={handleLogout}>{t("Abmelden", "Log out")}</Button>
                </div>
            </Card>
        </div>
    );
};

export default ProfilePage;
